// frontend/src/services/onboardingService.ts
// API layer for the AI onboarding module.
//
// Every function here is a thin adapter over /api/v2/onboarding: no field
// mapping is done inline in components (project rule 12). Read paths degrade
// to an empty-but-valid shape when the backend is unreachable, so the wizard
// still renders instead of blanking out (project rule 13).

import api from './api';
import { compressImageForUpload } from '../utils/imageCompression';
import type {
  OnboardingStatusPayload, OnboardingSession, SessionDetail, SourceDocumentSummary,
  ExtractionDraft, OnboardingQuestion, LocationParameter, AuditReport, RecipeCost,
  PosProgrammingRow, CommitOutcome, DraftStatus, DraftEntityType, SourceDocumentKind,
  SoldItemCost, PriceHistory, VolumeRebateRule,
} from '../types/onboarding';

// =============================================================================
// Offline-safe defaults
// =============================================================================

export const EMPTY_STATUS: OnboardingStatusPayload = {
  ai: {
    provider: 'none', configured: false, model: 'manuel', effort: 'high',
    maxTokens: 16000, fallbackModel: null, baseUrl: null, environment: 'unknown',
  },
  session: null,
  inventory: { resources: 0, recipes: 0, posItems: 0, unlinkedPosItems: 0, pricedResources: 0, pricedRatio: 0 },
  blockingFindings: 0,
  // False = offline placeholder. Callers that ACT on emptiness (the onboarding
  // call-to-action on the inventory page) must not mistake an unreachable
  // backend for an empty platform.
  reachable: false,
};

/** Read paths never throw: an unreachable backend renders an empty screen, not a crash. */
async function safeGet<T>(path: string, fallback: T): Promise<T> {
  try {
    const response = await api.get(path);
    return (response.data?.data ?? fallback) as T;
  } catch {
    return fallback;
  }
}

// =============================================================================
// Status
// =============================================================================

export async function fetchOnboardingStatus(): Promise<OnboardingStatusPayload> {
  const status = await safeGet<OnboardingStatusPayload>('/onboarding/status', EMPTY_STATUS);
  return status === EMPTY_STATUS ? status : { ...status, reachable: true };
}

export async function testAiConnection(): Promise<{ ok: boolean; model: string; latencyMs: number; message: string }> {
  const response = await api.post('/onboarding/ai/test');
  return response.data.data;
}

// =============================================================================
// Sessions
// =============================================================================

export async function fetchSessions(): Promise<OnboardingSession[]> {
  return safeGet<OnboardingSession[]>('/onboarding/sessions', []);
}

export async function fetchSession(id: string): Promise<SessionDetail | null> {
  return safeGet<SessionDetail | null>(`/onboarding/sessions/${id}`, null);
}

export async function createSession(name?: string): Promise<OnboardingSession> {
  const response = await api.post('/onboarding/sessions', name ? { name } : {});
  return response.data.data;
}

export async function updateSession(
  id: string,
  patch: { stage?: string; status?: string }
): Promise<OnboardingSession> {
  const response = await api.patch(`/onboarding/sessions/${id}`, patch);
  return response.data.data;
}

// =============================================================================
// Documents
// =============================================================================

/** Strip the `data:<mime>;base64,` prefix a FileReader produces. */
export function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(stripDataUrl(String(reader.result)));
    reader.onerror = () => reject(new Error(`Lecture impossible : ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * L'envoi se fait en lots bornés, séquentiels, et le budget se mesure sur la
 * taille RÉELLE du corps HTTP (le base64, +33 % par rapport au fichier) —
 * c'est lui que nginx pèse (plafond vivant : 25 Mo). Deux leçons de terrain :
 * un fichier seul de 20 Mo faisait 27 Mo de base64 et faisait sauter SON lot
 * en 413, et le lot en échec arrêtait tous les suivants (12/21 déposés).
 * Maintenant : le fichier trop gros est NOMMÉ et écarté, chaque lot vit sa
 * vie, et l'appelant reçoit le détail des succès ET des échecs.
 */
// Marge sous nginx (50m appliqué sur le VPS le 2026-08-31, JSON compris) :
// un fichier de 20 Mo (~27 Mo en base64) passe en un seul envoi. Si le VPS
// devait revenir à 25m, redescendre à 22 * 1024 * 1024.
const MAX_POST_BASE64_BYTES = 45 * 1024 * 1024;
const MAX_CHUNK_FILES = 6;
const MAX_FILE_BYTES = 20 * 1024 * 1024;

export interface UploadOutcome {
  documents: SourceDocumentSummary[];
  /** Fichiers non déposés, avec la raison — l'interface les nomme. */
  failures: Array<{ files: string[]; reason: string }>;
  /** Entrées de zip ignorées côté serveur (déjà formulées en français). */
  warnings: string[];
}

export async function uploadDocuments(
  sessionId: string,
  files: File[]
): Promise<UploadOutcome> {
  const failures: UploadOutcome['failures'] = [];

  const oversize = files.filter((f) => f.size > MAX_FILE_BYTES);
  if (oversize.length > 0) {
    failures.push({
      files: oversize.map((f) => f.name),
      reason: 'au-dessus de 20 Mo — compressez ou divisez le fichier',
    });
  }

  const documents: Array<{ filename: string; mimeType: string; content: string }> = [];
  for (const raw of files) {
    if (raw.size > MAX_FILE_BYTES) continue;
    // Photos are pre-compressed when it helps (see utils/imageCompression):
    // smaller upload, smaller stored row, and dimensions the vision API can
    // actually use. PDFs/CSVs and already-small images pass through as-is.
    const { file, original, beforeBytes, afterBytes } = await compressImageForUpload(raw);
    if (!original) {
      console.info(
        `[onboarding] « ${raw.name} » compressé : ${Math.round(beforeBytes / 1024)} Ko → ${Math.round(afterBytes / 1024)} Ko`
      );
    }
    const content = await readFileAsBase64(file);
    if (content.length > MAX_POST_BASE64_BYTES) {
      // Ce fichier seul dépasserait le plafond du serveur : l'envoyer
      // garantirait un 413. On l'écarte en le nommant, les autres passent.
      failures.push({
        files: [raw.name],
        reason: 'trop volumineux pour un envoi — compressez le fichier ou divisez-le',
      });
      continue;
    }
    documents.push({
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      content,
    });
  }

  // Regrouper en lots ≤ MAX_CHUNK_FILES fichiers et ≤ MAX_POST_BASE64_BYTES.
  const chunks: Array<typeof documents> = [];
  let current: typeof documents = [];
  let currentBytes = 0;
  for (const doc of documents) {
    const bytes = doc.content.length;
    if (current.length > 0 && (current.length >= MAX_CHUNK_FILES || currentBytes + bytes > MAX_POST_BASE64_BYTES)) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(doc);
    currentBytes += bytes;
  }
  if (current.length > 0) chunks.push(current);

  const created: SourceDocumentSummary[] = [];
  const warnings: string[] = [];
  // File d'envoi AUTO-ADAPTATIVE : un 413 (limite serveur, quelle qu'elle
  // soit) coupe le lot en deux et réessaie, jusqu'au fichier seul — le client
  // n'a plus à connaître la config nginx pour réussir. Seul un fichier SEUL
  // encore refusé est abandonné, nommé, avec le conseil.
  const queue: Array<typeof documents> = [...chunks];
  while (queue.length > 0) {
    const chunk = queue.shift()!;
    try {
      const response = await api.post(`/onboarding/sessions/${sessionId}/documents`, { documents: chunk });
      created.push(...(response.data.data as SourceDocumentSummary[]));
      warnings.push(...((response.data.warnings as string[] | undefined) ?? []));
    } catch (error) {
      const message = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
      if (message?.status === 413 && chunk.length > 1) {
        const mid = Math.ceil(chunk.length / 2);
        queue.unshift(chunk.slice(0, mid), chunk.slice(mid));
        continue;
      }
      // Un lot en échec ne condamne jamais les suivants : on note qui a
      // échoué et pourquoi, et on continue.
      failures.push({
        files: chunk.map((d) => d.filename),
        reason: message?.status === 413
          ? 'trop volumineux pour le serveur même seul — compressez ou divisez ce fichier'
          : message?.data?.message ?? 'échec réseau — redéposez ces fichiers',
      });
    }
  }
  return { documents: created, failures, warnings };
}

export async function createMobileToken(
  sessionId: string
): Promise<{ token: string; sessionName: string; expiresInMinutes: number; url: string }> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/mobile-token`);
  const data = response.data.data as { token: string; sessionName: string; expiresInMinutes: number };
  // Token in the FRAGMENT: browsers never send it to the server, so it stays
  // out of nginx and application logs.
  return { ...data, url: `${window.location.origin}/depot#${data.token}` };
}

/**
 * Contenu brut du document via le client API (jeton + rafraîchissement) —
 * une URL nue dans un <img src> partirait sans en-tête Authorization.
 */
export async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const response = await api.get(`/onboarding/documents/${documentId}/content`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

export type MenuItemState = 'ready' | 'attention' | 'blocked';
export interface MenuOverviewItem {
  name: string; section: string | null; variantLabel: string | null;
  /** Prix du menu (taxes incluses) et prix avant taxes dérivé. */
  sellPrice: number | null; sellPriceNet?: number | null;
  type: 'RECIPE' | 'RESALE'; draftStatus: string;
  state: MenuItemState; missing: string[]; target: string | null;
  /** Comment la cible a été trouvée (NAME_PARTIAL = inclusion ambiguë, à confirmer). */
  linkMethod?: 'LINKED' | 'NAME' | 'NAME_PARTIAL' | null;
  /** Item RECETTE sans recette ni composition : « Recette à définir ». */
  recipeStatus?: 'to_define' | null;
}
export interface MenuOverview {
  total: number; ready: number; attention: number; blocked: number;
  sections: Array<{ section: string; items: MenuOverviewItem[] }>;
}

/** Le menu complet vu comme un tableau d'avancement, item par item. */
export async function fetchMenuOverview(sessionId: string): Promise<MenuOverview | null> {
  return safeGet<MenuOverview | null>(`/onboarding/sessions/${sessionId}/menu-overview`, null);
}

export interface CoverageFinding {
  level: 'warning' | 'info';
  title: string;
  detail: string;
}
export interface CoverageSection {
  section: string; total: number; covered: number; uncoveredNames: string[];
}
export interface SessionCoverage {
  documents: {
    factures: number; catalogues: number; menus: number; recettes: number;
    nonClasses: number; enEchec: number; nonLus: number;
  };
  sections: CoverageSection[];
  findings: CoverageFinding[];
  /** La caisse Véloce comme cadre de référence (absent sur un serveur antérieur). */
  caisse?: { disponible: boolean; items: number; apparies: number; sansCorrespondance: string[] };
}

/** Le détecteur de documents manquants : quoi aller chercher, nommément. */
export async function fetchSessionCoverage(sessionId: string): Promise<SessionCoverage | null> {
  return safeGet<SessionCoverage | null>(`/onboarding/sessions/${sessionId}/coverage`, null);
}

/** Accepte d'un coup tous les brouillons sûrs de la session (côté serveur). */
export async function acceptSafeDrafts(sessionId: string): Promise<{ accepted: number }> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/accept-safe`);
  return response.data.data as { accepted: number };
}

/** Supprime une session vide ou abandonnée (refusé si des éléments ont été écrits). */
export async function deleteSession(sessionId: string): Promise<void> {
  await api.delete(`/onboarding/sessions/${sessionId}`);
}

export interface ResetOutcome {
  sessions: number; documents: number; posItems: number; recipes: number;
  resources: number; aliases: number; parameters: number; parametersSeeded: number;
}

/**
 * REMISE À ZÉRO de l'emplacement : efface tout ce que les intégrations ont
 * écrit et re-sème les paramètres par défaut. Admin seulement; le serveur
 * refuse si l'emplacement a de vraies livraisons ou commandes.
 */
export async function resetLocation(): Promise<ResetOutcome> {
  const response = await api.post('/onboarding/reset-location', { confirm: 'EFFACER' });
  return response.data.data as ResetOutcome;
}

/** Télécharge tous les originaux de la session en un zip (via le client authentifié). */
export async function downloadSessionArchive(sessionId: string): Promise<void> {
  const response = await api.get(`/onboarding/sessions/${sessionId}/documents-archive`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `documents-session-${new Date().toISOString().slice(0, 10)}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Classeur Excel « corrigé » de la session (Guide, Alertes, Ventes, Recettes,
 * Produits, Factures, Paramètres) — photographie des données au moment du clic.
 */
export async function downloadSessionWorkbook(sessionId: string): Promise<void> {
  const response = await api.get(`/onboarding/sessions/${sessionId}/export.xlsx`, { responseType: 'blob' });
  const disposition = String(response.headers?.['content-disposition'] ?? '');
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `coutant-recettes-prix_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Rendu texte d'un tableur (xlsx/csv/txt) — la même conversion que reçoit l'IA. */
export async function fetchDocumentText(documentId: string): Promise<string> {
  const response = await api.get(`/onboarding/documents/${documentId}/content`, {
    params: { as: 'text' },
    responseType: 'text',
    transformResponse: [(data) => data],
  });
  return String(response.data);
}

export async function setDocumentKind(
  documentId: string,
  kind: SourceDocumentKind
): Promise<SourceDocumentSummary> {
  const response = await api.patch(`/onboarding/documents/${documentId}`, { kind });
  return response.data.data;
}

export async function classifyDocument(documentId: string): Promise<{ kind: SourceDocumentKind; rationale: string }> {
  const response = await api.post(`/onboarding/documents/${documentId}/classify`);
  return response.data.data;
}

export interface RereadSummary {
  paired: number; changed: number; toReconfirm: number; orphansKept: number; deleted: number; created: number; linksReset: number;
  /** Rangées modifiées par quelqu'un pendant la lecture : décision courante conservée, note posée. */
  concurrent?: number;
}

export async function extractDocument(documentId: string): Promise<{
  documentId: string; kind: SourceDocumentKind; draftsCreated: number; questionsCreated: number;
  /** Présent quand le document avait déjà été lu : sa lecture a été remplacée. */
  reread?: RereadSummary;
}> {
  const response = await api.post(`/onboarding/documents/${documentId}/extract`);
  return response.data.data;
}

/** Recalcul SANS relecture (gratuit) d'un document déjà lu : parseur actuel sur l'extraction stockée. */
export async function renormalizeDocument(documentId: string): Promise<{
  documentId: string; kind: SourceDocumentKind; draftsCreated: number; questionsCreated: number; reread?: RereadSummary;
}> {
  const response = await api.post(`/onboarding/documents/${documentId}/renormalize`);
  return response.data.data;
}

export interface RenormalizeSessionSummary extends RereadSummary {
  documents: number;
  failures: Array<{ filename: string; error: string }>;
  rematch: RematchSummary | null;
}

/** Recalcul SANS relecture de tous les documents lus de la session, puis rapprochement. */
/**
 * Lance le recalcul de session en TÂCHE DE FOND (202) : le bilan se lit
 * ensuite sur la session (`recalcul`), voir useRenormalizeSession.
 */
export async function renormalizeSession(sessionId: string): Promise<{ queued: boolean; documents: number; demarreLe: string | null }> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/renormalize`);
  return response.data.data as { queued: boolean; documents: number; demarreLe: string | null };
}

export async function extractAll(
  sessionId: string,
  options: { reread?: boolean } = {}
): Promise<{ queued: number; alreadyRunning: boolean }> {
  // Fire-and-return: the server queues the batch and processes it in the
  // background; progress arrives through the polled per-document statuses.
  // reread : relire aussi les documents déjà lus (lecture remplacée, décisions conservées).
  const response = await api.post(`/onboarding/sessions/${sessionId}/extract-all${options.reread ? '?relire=1' : ''}`);
  return response.data.data;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/onboarding/documents/${documentId}`);
}

export interface RematchSummary {
  productsUpdated: number;
  productsConfirmed: number;
  recipeLinesLinked: number;
  recipesUpdated: number;
  aiLinesLinked: number;
  questionsAutoAnswered?: number;
  aiPasses?: { formatsRepaired: number; menuItemsLinked: number; menuRecipesDeduced: number; namesCanonicalized?: number };
  autoAccepted?: number;
  autoRejected?: number;
  familyPriced?: number;
  familyPending?: number;
  menuColumnsCollapsed?: number;
  posMatched?: number;
  posGrouped?: number;
  posVariantsCreated?: number;
  policies?: { brandLinesLinked: number; garnishLinesSettled: number; rebatesApplied: number };
}

/** Rejoue correspondances et confiance sur les brouillons en attente — sans IA. */
export async function rematchSession(sessionId: string): Promise<RematchSummary> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/rematch`);
  return response.data.data as RematchSummary;
}

export interface CleanupCandidate {
  id: string;
  label: string;
  motif: string;
  packPrice: number | null;
  /** Qui a signalé la ligne : une règle (motif dans le libellé) ou le modèle. */
  source: 'regle' | 'ia';
  /** Pourquoi, en clair. */
  explication: string;
  rawDescription: string | null;
  formatText: string | null;
  quantity: number | null;
  lineTotal: number | null;
  supplierName: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  /** Document d'origine (pour l'ouvrir) et position de la ligne dedans. */
  document: { id: string; filename: string } | null;
  page: number | null;
  line: number | null;
}

/** Lignes qui ne sont probablement pas des produits (consignes, dépôts, frais…). */
export async function fetchCleanupCandidates(sessionId: string): Promise<CleanupCandidate[]> {
  const response = await api.get(`/onboarding/sessions/${sessionId}/cleanup-candidates`);
  return response.data.data as CleanupCandidate[];
}

export interface MissingProductCandidate {
  key: string;
  name: string;
  occurrences: number;
  recipes: string[];
  consumptionUnit: 'ml' | 'g' | 'unit';
  /** D'où vient le besoin : lignes de recettes, ou item de menu sans source. */
  origin?: 'recette' | 'menu';
  /** Typage de l'item de menu d'origine (RECETTE = vendu au shot, sans fiche). */
  menuType?: 'RECIPE' | 'RESALE' | null;
  /** Catégorie devinée par règles ; « Autre » = reliquat (IA à la création). */
  category?: string;
  /** Fournisseur probable (SAQ, Alex Coulombe (Pepsi), Beaudry…). */
  supplierName?: string | null;
  /** Format parsé depuis le nom, ou format par défaut (assumed). */
  format?: { packUnits: number; unitSize: number; unitSizeUom: string; text: string; assumed: boolean } | null;
  /** Coché par défaut (faux pour un item que rien ne classe : probablement une recette à écrire). */
  suggested?: boolean;
  /** Correspondance dans le répertoire fournisseurs partagé (SAQ) : prix estimé disponible. */
  catalog?: {
    source: string; sourceCode: string; name: string;
    priceNet: number | null; sizeMl: number | null; sizeText: string | null; upc: string | null;
  } | null;
}

/** Ingrédients de recettes sans produit nulle part (fruits frais, épicerie…). */
export async function fetchMissingProducts(sessionId: string): Promise<MissingProductCandidate[]> {
  const response = await api.get(`/onboarding/sessions/${sessionId}/missing-products`);
  return response.data.data as MissingProductCandidate[];
}

export interface MissingProductInput {
  name: string;
  consumptionUnit: 'ml' | 'g' | 'unit';
  /** Prix estimé du format d'achat (à confirmer par facture) — ESTIMÉ, jamais confirmé. */
  estimatedPrice?: number | null;
}

/** Crée les produits confirmés (sans prix, ou au prix estimé saisi) puis rejoue le rapprochement. */
export async function createMissingProducts(
  sessionId: string,
  items: MissingProductInput[]
): Promise<{ created: number; rematch: RematchSummary; aiClassified?: number }> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/missing-products`, { items });
  return response.data.data as { created: number; rematch: RematchSummary; aiClassified?: number };
}

/** CSV « items sans source » (catégorie, fournisseur probable, format) — la liste de courses. */
export async function downloadMissingProductsCsv(sessionId: string): Promise<void> {
  const response = await api.get(`/onboarding/sessions/${sessionId}/missing-products.csv`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `items_sans_source_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// =============================================================================
// Initialisation du stock depuis les factures déposées
// =============================================================================

export interface StockInitStatus {
  eligible: number;
  alreadyApplied: number;
  unconvertible: string[];
  answer: string | null;
  questionId: string | null;
}

export interface StockInitOutcome {
  created: number;
  skipped: number;
  unconvertible: Array<{ name: string; reason: string }>;
}

export async function fetchStockInitStatus(sessionId: string): Promise<StockInitStatus | null> {
  return safeGet<StockInitStatus | null>(`/onboarding/sessions/${sessionId}/stock-init`, null);
}

/** Entre le stock des factures écrites (mouvements d'achat datés, incrément atomique, idempotent). */
export async function runStockInit(sessionId: string): Promise<StockInitOutcome> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/stock-init`);
  return response.data.data as StockInitOutcome;
}

// =============================================================================
// Validation queue
// =============================================================================

export interface DraftQuery {
  status?: DraftStatus | 'all';
  entityType?: DraftEntityType | 'all';
  search?: string;
  take?: number;
  skip?: number;
}

export async function fetchDrafts(
  sessionId: string,
  query: DraftQuery = {}
): Promise<{ drafts: ExtractionDraft[]; total: number; counts: Record<string, number>; safePending: number }> {
  const params = new URLSearchParams();
  if (query.status && query.status !== 'all') params.set('status', query.status);
  if (query.entityType && query.entityType !== 'all') params.set('entityType', query.entityType);
  if (query.search) params.set('search', query.search);
  if (query.take) params.set('take', String(query.take));
  if (query.skip) params.set('skip', String(query.skip));

  try {
    const response = await api.get(`/onboarding/sessions/${sessionId}/drafts?${params.toString()}`);
    return {
      drafts: response.data.data ?? [],
      total: response.data.meta?.total ?? 0,
      counts: response.data.meta?.counts ?? {},
      safePending: response.data.meta?.safePending ?? 0,
    };
  } catch {
    return { drafts: [], total: 0, counts: {}, safePending: 0 };
  }
}

export async function reviewDraft(
  draftId: string,
  body: { action: 'accept' | 'reject' | 'edit'; payload?: Record<string, unknown>; matchedResourceId?: string | null }
): Promise<ExtractionDraft> {
  const response = await api.patch(`/onboarding/drafts/${draftId}`, body);
  return response.data.data;
}

export async function bulkReviewDrafts(
  ids: string[],
  action: 'accept' | 'reject'
): Promise<{ updated: number; skipped: Array<{ id: string; label: string; reason: string }> }> {
  const response = await api.post('/onboarding/drafts/bulk', { ids, action });
  return response.data.data;
}

// =============================================================================
// Questions
// =============================================================================

export async function fetchQuestions(sessionId: string): Promise<OnboardingQuestion[]> {
  return safeGet<OnboardingQuestion[]>(`/onboarding/sessions/${sessionId}/questions`, []);
}

export async function answerQuestion(
  questionId: string,
  answer: string
): Promise<{ parameterUpdated: boolean; parameterKey: string | null; value: number | null }> {
  const response = await api.post(`/onboarding/questions/${questionId}/answer`, { answer });
  return response.data.data;
}

// =============================================================================
// Commit & audit
// =============================================================================

export async function commitSession(
  sessionId: string
): Promise<{ commit: CommitOutcome; audit: AuditReport }> {
  const response = await api.post(`/onboarding/sessions/${sessionId}/commit`);
  return response.data.data;
}

export async function fetchAudit(sessionId?: string | null): Promise<AuditReport | null> {
  const suffix = sessionId ? `?sessionId=${sessionId}` : '';
  return safeGet<AuditReport | null>(`/onboarding/audit${suffix}`, null);
}

// =============================================================================
// Costs
// =============================================================================

export async function fetchCosts(): Promise<RecipeCost[]> {
  return safeGet<RecipeCost[]>('/onboarding/costs', []);
}

/** Feuille « Ventes » : coût, cible, prix suggéré et diagnostic par item de caisse. */
export async function fetchSoldItemCosts(): Promise<SoldItemCost[]> {
  return safeGet<SoldItemCost[]>('/onboarding/costs/items', []);
}

/** Historique des prix d'un produit par date de facture. */
export async function fetchPriceHistory(resourceId: string): Promise<PriceHistory | null> {
  return safeGet<PriceHistory | null>(`/onboarding/resources/${resourceId}/price-history`, null);
}

// =============================================================================
// Règles d'escompte de volume
// =============================================================================

export async function fetchRebateRules(): Promise<VolumeRebateRule[]> {
  return safeGet<VolumeRebateRule[]>('/onboarding/rebate-rules', []);
}

export async function saveRebateRule(body: {
  id?: string | null;
  supplierPattern: string;
  amountPerCase: number;
  mlPerCase?: number | null;
  categoryPatterns?: string[] | null;
  confidence?: 'CONFIRMED' | 'ESTIMATED';
}): Promise<VolumeRebateRule & { resourcesUpdated: number }> {
  const { id, ...payload } = body;
  const response = id
    ? await api.put(`/onboarding/rebate-rules/${id}`, payload)
    : await api.post('/onboarding/rebate-rules', payload);
  return response.data.data;
}

// =============================================================================
// Parameters
// =============================================================================

export async function fetchParameters(): Promise<LocationParameter[]> {
  return safeGet<LocationParameter[]>('/onboarding/parameters', []);
}

export async function saveParameter(body: {
  key: string;
  label: string;
  value: number;
  unit?: string | null;
  category?: string | null;
  description?: string | null;
  isIndexable?: boolean;
}): Promise<LocationParameter> {
  const response = await api.post('/onboarding/parameters', body);
  return response.data.data;
}

export async function deleteParameter(id: string): Promise<void> {
  await api.delete(`/onboarding/parameters/${id}`);
}

export async function indexParameters(percent: number): Promise<{ updated: number }> {
  const response = await api.post('/onboarding/parameters/index', { percent });
  return response.data.data;
}

// =============================================================================
// POS programming
// =============================================================================

export async function fetchPosItems(): Promise<PosProgrammingRow[]> {
  return safeGet<PosProgrammingRow[]>('/onboarding/pos-items', []);
}

export async function syncPosItems(): Promise<{ created: number; updated: number }> {
  const response = await api.post('/onboarding/pos-items/sync');
  return response.data.data;
}

export async function updatePosItem(
  id: string,
  patch: Record<string, unknown>
): Promise<PosProgrammingRow> {
  const response = await api.patch(`/onboarding/pos-items/${id}`, patch);
  return response.data.data;
}

/** Downloads the register-programming CSV through the authenticated client. */
export async function downloadPosCsv(): Promise<void> {
  const response = await api.get('/onboarding/pos-items?format=csv', { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `programmation_veloce_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
